function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _type_of(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import hexToRgba from "hex-to-rgba";
import { memo, useCallback, useContext } from "react";
import { AccessibilityInfo, findNodeHandle, Keyboard, Platform } from "react-native";
import { Markdown } from "./Markdown";
import { ScrollViewContext } from "./ScrollView";
import { Text } from "./Text";
import { UnorderedList } from "./UnorderedList";
import { View } from "./View";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../styles";
var ERROR_LOOKUP = {
    EMAIL_IN_USE: 'A user already exists for the given email.',
    DUPLICATE: 'A user already exists for the given email.',
    USER_EXISTS: 'A user already exists for the given email.',
    INVALID_EMAIL: 'The email provided is not valid.',
    NOT_FOUND: 'This record could not be found.',
    UNAUTHORIZED: 'You are not authorized to perform this request.'
};
var TEXT_COLOR = '#932a2a';
/**
 * Renders various types of errors to the user in an accessible way. Can be
 * generic string errors, form errors, or error objects.
 *
 * When an error is presented, the screen will be scrolled to show the error in
 * view if we are rendering inside of a ScrollView.
 *
 * Dev Notes:
 * We use memo() here because inline ref callbacks are called on every render vs class
 * style react where they are called only on mount/unmount.
 * We don't want to useCallback for our ref callback because then we wouldn't run if errorString or
 * error are changed
 * https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-86}
 */ export var ErrorPresenter = /*#__PURE__*/ memo(function(param) {
    var error = param.error, errorString = param.errorString, formErrors = param.formErrors, style = param.style;
    var theme = useTheme().theme;
    var scrollToElement = useContext(ScrollViewContext).scrollToElement;
    var $t = useI18n().$t;
    var ref = useCallback(function(r) {
        if (r) {
            if (Platform.OS !== 'web') {
                Keyboard.dismiss();
            }
            scrollToElement(r);
            setTimeout(function() {
                if (Platform.OS !== 'web') {
                    var handle = findNodeHandle(r);
                    if (handle) {
                        // If the element requires scrolling to be visible, we must wait for scrolling
                        // before setAccessibilityFocus. Otherwise focus isn't properly performed.
                        AccessibilityInfo.setAccessibilityFocus(handle);
                    }
                }
            }, 250);
        }
    }, [
        scrollToElement
    ]);
    return errorString || error || Object.keys(formErrors !== null && formErrors !== void 0 ? formErrors : {}).length ? /*#__PURE__*/ _jsxs(View, {
        style: [
            {
                padding: 16,
                paddingHorizontal: 36,
                borderRadius: 8,
                backgroundColor: hexToRgba(theme.color.danger, 0.1)
            },
            style
        ],
        accessible: true,
        ref: ref,
        spacing: 8,
        testID: "ErrorPresenter",
        children: [
            errorString ? /*#__PURE__*/ _jsxs(View, {
                spacing: 8,
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        text: "Error",
                        weight: "semibold",
                        color: TEXT_COLOR
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        text: errorString,
                        color: TEXT_COLOR
                    })
                ]
            }) : (error === null || error === void 0 ? void 0 : error.graphQLErrors.length) ? error.graphQLErrors.map(function(e, i) {
                var _e_extensions;
                return /*#__PURE__*/ _jsx(View, {
                    children: /*#__PURE__*/ _jsx(Text, {
                        text: ERROR_LOOKUP[e.message] || ERROR_LOOKUP[(_e_extensions = e.extensions) === null || _e_extensions === void 0 ? void 0 : _e_extensions.message] || 'An unknown error has occurred.',
                        color: TEXT_COLOR
                    })
                }, i);
            }) : error ? /*#__PURE__*/ _jsxs(View, {
                spacing: 8,
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        text: "Error",
                        weight: "semibold",
                        color: TEXT_COLOR
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        text: "Something went wrong",
                        color: TEXT_COLOR
                    })
                ]
            }) : null,
            formErrors && Object.keys(formErrors).length ? /*#__PURE__*/ _jsxs(View, {
                spacing: 8,
                children: [
                    /*#__PURE__*/ _jsx(Markdown, {
                        style: {
                            text: {
                                color: TEXT_COLOR
                            }
                        },
                        children: $t({
                            id: 'ErrorPresenter_fieldErrorsHeading',
                            defaultMessage: '**{numErrors, plural, one{1 error} other{# errors}}** in the following fields:'
                        }, {
                            numErrors: Object.keys(formErrors).length
                        })
                    }),
                    /*#__PURE__*/ _jsx(View, {
                        style: {
                            marginLeft: 8
                        },
                        children: /*#__PURE__*/ _jsx(UnorderedList, {
                            size: "small",
                            color: TEXT_COLOR,
                            textColor: TEXT_COLOR,
                            weight: "normal",
                            items: Object.entries(formErrors).map(function(param) {
                                var _param = _sliced_to_array(param, 2), label = _param[0], value = _param[1];
                                if ((typeof value === "undefined" ? "undefined" : _type_of(value)) === 'object') {
                                    return "".concat(value.label, ": ").concat(value.message);
                                }
                                label = label.replace(' *', '').replace('*', '');
                                return value ? "".concat(label, ": ").concat(value) : label;
                            })
                        })
                    })
                ]
            }) : null
        ]
    }) : null;
});
